"use strict";

var xtend = require("xtend");
var window = require("global");
var EventEmitter = require("eventemitter2").EventEmitter2;
var RTCPeerConnection = window.RTCPeerConnection;
var ReactNative = require("react-native");
var Dimensions = ReactNative.Dimensions;
var optimize = require("./sdp_optimization");
var analyzeStats = require("./analyzeStats").analyzeStats;
var asap = require("asap");

module.exports = {
	type: "browser",
	init: init,
	RTC: RTC
};

var cachedStream = {};
var cacheId = "";

function RTC(options){
	this.options = options;
	this.limitBitrate = false;
	this.dropped = 3;
	var ice = options.iceServers || [{
		"urls": "stun:stun.1.google.com:19302"
	}];
	console.debug("Making PeerConnection with ICE:", ice);
	EventEmitter.call(this);
	initiateRTC.call(this, options);
	if(options.isInitiator)
		setTimeout(function(){
			this._sendOffer().catch(this._onerr);
		}.bind(this));
}

function initiateRTC(options){
	var stream = options._local;
	this._onerr = handleError.bind(this);
	this.pc = new RTCPeerConnection({
		iceServers: options.iceServers
	});
	this.pc.ontrack = this._handleStream.bind(this);
	this.pc.onicecandidate = this._handleLocalIce.bind(this);
	this.pc.oniceconnectionstatechange = this._handleConnectionStateChange.bind(this);
	this.pc.onremovestream = this.clean.bind(this);
	stream.getTracks().forEach(function(track){
		return this.pc.addTrack(track, stream);
	}.bind(this));
	console.debug("Adding stream to local peer connection");
}

RTC.prototype = new EventEmitter();
RTC.prototype.options = null;
RTC.prototype.pc = null;
RTC.prototype.dropped = 3;
RTC.prototype.limitBitrate = false;

RTC.prototype.limitBandwidth = function(){
	if(this.dropped !== 1) this.dropped--;
	this.limitBitrate = true;
};

RTC.prototype.getStats = function(){
	return this.pc.getStats().then(function(stats){
		return analyzeStats(stats);
	});
};

RTC.prototype.addICE = function(ice){
	var pc = this.pc;
	console.debug("Adding ICE candidate", ice);
	return pc.addIceCandidate(new window.RTCIceCandidate({
		candidate: ice.candidate || "candidate",
		sdpMLineIndex: ice.label,
		sdpMid: ice.id
	}));
};

RTC.prototype.addSDP = function(sdp){
	var initiator = this.options.isInitiator;
	if(initiator) return this._handleAnswer(sdp);
	else return this._handleOffer(sdp);
};

RTC.prototype.filterSDP = function(sdp){
	var initiator = this.options.isInitiator;
	sdp = optimize(sdp, initiator, this.limitBitRate, this.dropped);
	return sdp;
};

RTC.prototype.clean = function(){
	console.debug("Cleaning Browser RTC");
	var pc = this.pc;
	var options = this.options;
	if(this._isOpen()) pc.close();
	if(this._remote) this._remote = null;
	this.emit("close");
	releaseStream(options);
};

RTC.prototype._isOpen = function(){
	var pc = this.pc;
	if(pc && pc.signalingState && pc.signalingState === "closed") return false;
	return pc.iceConnectionState !== "closed";
};

RTC.prototype._sendOffer = function(){
	var pc = this.pc;
	return pc.createOffer().then(function(offer){
		console.debug("Created offer", offer);
		var sdp = this.filterSDP(offer.sdp);
		var newOffer = new window.RTCSessionDescription({
			type: offer.type,
			sdp: sdp
		});
		return pc.setLocalDescription(newOffer).then(function(){
			this.emit("sdp", {
				type: offer.type,
				sdp: sdp
			});
			console.debug("Emitted offer", newOffer);
		}.bind(this));
	}.bind(this));
};

RTC.prototype._handleConnectionStateChange = function(){
	var state = this.pc.iceConnectionState;
	console.debug("Changed connection state", state);
	if(["closed", "disconnected", "failed"].indexOf(state) !== -1)
		this.clean();
};

RTC.prototype._handleLocalIce = function(event){
	var ice = event.candidate;
	console.debug("Handling local ICE candidate", ice);
	if(ice && this._isOpen()) this.emit("ice", {
		type: "candidate",
		candidate: ice.candidate,
		label: ice.sdpMLineIndex,
		id: ice.sdpMid
	});
};

RTC.prototype._handleOffer = function(sdp){
	var pc = this.pc;
	console.debug("Handling SDP offer", sdp);
	return pc.setRemoteDescription(new window.RTCSessionDescription({
		type: sdp.type,
		sdp: sdp.sdp
	})).then(function(){
		return pc.createAnswer().then(function(answer){
			console.debug("Created SDP Answer", answer);
			answer.sdp = this.filterSDP(answer.sdp);
			return pc.setLocalDescription(new window.RTCSessionDescription(answer)).then(function(){
				this.emit("sdp", {
					sdp: answer.sdp,
					type: answer.type
				});
				console.debug("Set local description", answer);
			}.bind(this));
		}.bind(this));
	}.bind(this));
};

RTC.prototype._handleAnswer = function(sdp){
	console.debug("Handling SDP answer", sdp);
	return this.pc.setRemoteDescription(new window.RTCSessionDescription({
		type: sdp.type,
		sdp: sdp.sdp
	}));
};

RTC.prototype._handleStream = function(event){
	var stream = event.streams[0];
	var options = this.options;

	if(this._remote !== stream) {
		this._remote = stream;
		this.emit("stream", {
			local: options._local,
			remote: stream
		});
	}
};

RTC.prototype.switchCamera = function (parent){
	var options = this.options;
	var pc = this.pc;

	var videoSource = window.localStorage.video_input;
	options.videoSource = videoSource;


	if(!pc || !options) Promise.reject(false);

	return getStream(options).then(function(stream){
		var videoTrack = stream.getVideoTracks()[0];
		var sender = pc.getSenders().find(function(s){
			return s.track.kind === videoTrack.kind;
		});
		console.debug("Switching camera to track", videoTrack);
		this.options._local = stream;
		sender.replaceTrack(videoTrack);
		setLocalStream.call(parent, stream);
		return stream;
	}.bind(this));
};

function setLocalStream(stream){
	this.stream = xtend(this.stream, {
		local: stream
	});
	asap(this.emit.bind(this, "stream", this.stream));
}

function init(options){
	options = options || {};
	console.debug("Initializing NEW Browser RTC", options);

	var audioSource = window.localStorage.audio_input;
	var videoSource = window.localStorage.video_input;

	options.audioSource = audioSource;
	options.videoSource = videoSource;
	return getStream(options).then(function(stream){
		console.debug("Got local stream", stream);
		options._local = stream;
		return new RTC(options);
	});
}

function getStream(options){
	var id = getId(options);
	if(cacheId !== id){
		releaseStream(options);
		cachedStream = getLocalStream(options).catch(handle_invalid);
		cacheId = id;
		return cachedStream;
	} else {
		return cachedStream;
	}
}

function getId(options){
	return "" + options.audioSource + options.videoSource + !!options.bigger;
}

function getLocalStream(options){
	var use_bigger = options.bigger;
	var noConstraints = options.noConstraints;

	var audioConstraints = {
		googAutoGainControl: false,
		googAutoGainControl2: false,
		googEchoCancellation: true,
		googEchoCancellation2: true,
		googNoiseSuppression: true,
		googNoiseSuppression2: true,
		googHighpassFilter: true,
		googTypingNoiseDetection: true,
		googAudioMirroring: true,
		googDucking: false,
	};

	var maxWidth = (use_bigger ? 640 : 320);
	var maxHeight = (use_bigger ? 480 : 240);
	var minVideo = 200;

	if(isPortrait()){
		var swap = maxWidth;
		maxWidth = maxHeight;
		maxHeight = swap;
	}

	var videoConstraints = {
		width: {
			min: minVideo,
			ideal: 320,
			max: maxWidth,
		},
		height: {
			min: minVideo,
			ideal: 320,
			max: maxHeight,
		},
		frameRate: {
			min: 1,
			max: 30
		}
	};

	var audioSource = options.audioSource;
	if (audioSource && (audioSource !== "null") && (audioSource !== "undefined"))
		audioConstraints.deviceId = {
			exact: audioSource
		};

	var videoSource = options.videoSource;
	if (videoSource && (videoSource !== "null") && (videoSource !== "undefined"))
		videoConstraints.deviceId = {
			exact: videoSource
		};

	var constraints = {
		audio: audioConstraints,
		video: videoConstraints
	};

	var voiceOnly = (videoSource === "false") || options.voiceOnly;

	if(noConstraints)
		constraints = {
			audio: true,
			video: true,
		};

	if (voiceOnly)
		constraints.video = false;

	return window.navigator.mediaDevices.getUserMedia(constraints);
}

function handleError(err){
	this.clean();
	this.emit("error", err);
}

function releaseStream(options){
	var localStream = options._local;
	if(localStream){
		var tracks = localStream.getTracks();
		tracks.forEach(function(track){
			return track.stop();
		});
		cacheId = "";
		cachedStream = null;
	}
}

function isPortrait() {
	var size = Dimensions.get("window");
	return size.width < size.height;
}

function handle_invalid(e) {
	var options = this.options;
	var isNotFound = (e.name === "NotFoundError");
	var constraint = e.constraint;
	var isDeviceConstraint = (constraint === "deviceId");
	var isOverconstrained = (e.name === "OverconstrainedError");

	if (isNotFound || (isOverconstrained && isDeviceConstraint)) {
		// Clear input options if they are invalid
		options.videoSource = "";
		options.audioSource = "";

		window.localStorage.video_input = "";
		window.localStorage.audio_input = "";

		return getStream(options).catch(handle_invalid);
	}

	var RESOLUTION_FIELDS = [
		"width",
		"minWidth",
		"height",
		"minHeight"
	];

	if (isOverconstrained && (RESOLUTION_FIELDS.indexOf(constraint) !== -1)) {
		return getStream(xtend(options, {
			bigger: true
		})).catch(handle_invalid);
	}

	return getStream(xtend(options, {
		bigger: true,
		noConstraints: true,
		videoSource: "",
		audioSource: ""
	}));
}
